package com.partejar.app.feature.relacionamentos.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.feature.entitlements.data.CommercialConstraintException
import com.partejar.app.feature.entitlements.presentation.EntitlementCoordinator
import com.partejar.app.feature.relacionamentos.data.*
import com.partejar.app.feature.workspace.presentation.commercialMessage
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

sealed interface RelacionamentosUiState {
    data object Loading : RelacionamentosUiState
    data class Content(
        val items: List<RelationshipItem>,
        val refreshing: Boolean = false,
        val hasNextPage: Boolean = false,
        val isLoadingNextPage: Boolean = false,
        val nextPageError: String? = null
    ) : RelacionamentosUiState
    data object Empty : RelacionamentosUiState
    data class Error(val message: String) : RelacionamentosUiState
}

class RelacionamentosViewModel private constructor(
    private val source: RelacionamentosDataSource,
    private val testScope: CoroutineScope?,
    @Suppress("UNUSED_PARAMETER") testable: Unit
) : ViewModel() {
    constructor(source: RelacionamentosDataSource) : this(source, null, Unit)
    internal constructor(source: RelacionamentosDataSource, scope: CoroutineScope) : this(source, scope, Unit)
    private val scope get() = testScope ?: viewModelScope
    private val _state = MutableStateFlow<RelacionamentosUiState>(RelacionamentosUiState.Loading)
    val state = _state.asStateFlow()
    private val _filter = MutableStateFlow(RelacionamentoFilter.ALL)
    val filter = _filter.asStateFlow()
    private var currentPage = 0
    private var firstPageJob: Job? = null
    private var nextPageJob: Job? = null

    init { refresh() }

    fun select(filter: RelacionamentoFilter) {
        if (_filter.value == filter) return
        _filter.value = filter
        refresh()
    }

    fun refresh() {
        nextPageJob?.cancel()
        firstPageJob?.cancel()
        firstPageJob = scope.launch { loadFirstPage() }
    }

    fun loadNextPage() {
        val current = _state.value as? RelacionamentosUiState.Content ?: return
        if (!current.hasNextPage || current.isLoadingNextPage) return
        val filter = _filter.value
        val page = currentPage + 1
        _state.value = current.copy(isLoadingNextPage = true, nextPageError = null)
        nextPageJob = scope.launch {
            try {
                val next = source.list(filter, page)
                if (filter != _filter.value) return@launch
                currentPage = page
                _state.value = current.copy(
                    items = (current.items + next.items).distinctBy { it.id },
                    hasNextPage = !next.last,
                    isLoadingNextPage = false,
                    nextPageError = null
                )
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                if (filter == _filter.value) {
                    _state.value = current.copy(
                        isLoadingNextPage = false,
                        nextPageError = "Não foi possível carregar mais relacionamentos."
                    )
                }
            }
        }
    }

    private suspend fun loadFirstPage() {
        val filter = _filter.value
        val old = _state.value as? RelacionamentosUiState.Content
        _state.value = old?.copy(refreshing = true, nextPageError = null) ?: RelacionamentosUiState.Loading
        try {
            val page = source.list(filter, 0)
            if (filter != _filter.value) return
            currentPage = 0
            _state.value = if (page.items.isEmpty()) RelacionamentosUiState.Empty else RelacionamentosUiState.Content(
                items = page.items.distinctBy { it.id },
                hasNextPage = !page.last
            )
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            if (filter == _filter.value) {
                _state.value = old ?: RelacionamentosUiState.Error("Não foi possível carregar os relacionamentos.")
            }
        }
    }
}

class NovoRelacionamentoViewModel(
    private val source: RelacionamentosDataSource,
    private val entitlementCoordinator: EntitlementCoordinator? = null
) : ViewModel() {
    private val _submitting = MutableStateFlow(false); val submitting = _submitting.asStateFlow()
    private val _error = MutableStateFlow<String?>(null); val error = _error.asStateFlow()
    fun create(input: NewRelationship, onCreated: (Long) -> Unit) = viewModelScope.launch {
        _submitting.value = true; _error.value = null
        val entitlement = entitlementCoordinator?.workspace?.value as? com.partejar.app.feature.entitlements.data.CommercialState.Available
        if (entitlement?.value?.patientLimit?.let { entitlement.value.patientsUsed >= it } == true) {
            _error.value = commercialMessage("LIMITE_PACIENTES_ATINGIDO")
            _submitting.value = false
            return@launch
        }
        runCatching { source.create(input) }.onSuccess {
            entitlementCoordinator?.refreshWorkspace()
            onCreated(it.id)
        }.onFailure { error ->
            _error.value = (error as? CommercialConstraintException)?.let { commercialMessage(it.code) }
                ?: "Não foi possível criar o contato."
        }
        _submitting.value = false
    }
}
